import * as React from 'react';
import withStyles from 'react-jss';
import { StylesProps, KeyLabelObject } from 'src/Library/Types';
import Row from 'antd/lib/row';
import Col from 'antd/lib/col';
import Form, { FormComponentProps } from 'antd/lib/form';
import InputNumber from 'antd/lib/input-number';
import Input from 'antd/lib/input';
import Radio from 'antd/lib/radio';
import Select from 'antd/lib/select';
import TextArea from 'antd/lib/input/TextArea';
import Button from 'antd/lib/button';
import Modal from 'antd/lib/modal';
import DatePicker from 'antd/lib/date-picker';
import message from 'antd/lib/message';
import { INTTRABookingConfirmationModalStyles } from './INTTRABookingConfirmationModalStyles';
import { prettifyString } from 'src/utils/utils';
import { connect } from 'react-redux';
import { getDateValue, isValidNumber } from 'src/Library/Helper';
import { ContainerList, Ports } from 'src/components/common/Types/CommonTypes';
import { BookingDetails, ChargeDetails } from './Booking.types';
import { useGenericState } from 'src/Library/UseGenericState';
import { useDebounce } from 'src/hooks/useDebounce';
import { renderSelectDropDown } from 'src/Library/Common';
import { getPorts } from 'src/api/containerTracking';
import Asterisk from 'src/components/common/Asterisk';

interface IProps extends
  StylesProps<ReturnType<typeof INTTRABookingConfirmationModalStyles>>, FormComponentProps {
  isVisible: boolean;
  bookingDetails: BookingDetails;
  chargeDetails: ChargeDetails;
  onCancel: () => void;
  haulage: KeyLabelObject [];
  containerList: ContainerList [];
  onSave: (newValues) => void;
  isAmendOrNew: boolean;
  weightsUnits: string [];
  containerTypeSizeTempRange;
}

interface StateProps {
  searchTerm: string;
  isPortLoading: boolean;
  ports: Ports [];
}

const EditINTTRAShipmentDetailsModal = (props: IProps): React.ReactElement => {

  const {
    classes,
    isVisible,
    form,
    bookingDetails,
    onCancel,
    haulage,
    chargeDetails,
    containerList,
    onSave,
    isAmendOrNew,
    weightsUnits,
    containerTypeSizeTempRange,
  } = props;

  const {
    getFieldDecorator,
  } = form;

  const [state, setState] = useGenericState<StateProps>({
    searchTerm: '',
    isPortLoading: false,
    ports: [],
  })

  const {
    searchTerm,
    isPortLoading,
    ports,
  } = state;

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  React.useEffect(
    () => {
      searchPorts(debouncedSearchTerm);
    },
    [debouncedSearchTerm],
  );

  const searchPorts = async (queryString: string) : Promise<void> => {
    setState({ isPortLoading: true });
    let ports : Ports [] = [];
    const response = await getPorts({
      queryString,
      type: ['WET'],
      searchByCountryName: true
    });
    if (response.isSuccess) {
      ports = response.data.map(el => ({
        code: el.code,
        name: el.name,
      }));
    } else {
      message.error(response.errorMessage);
    }
    setState({ ports, isPortLoading: false });
  };

  const renderMode = () : React.ReactElement => {
    return (
      <Row style={{ fontWeight: 600 }}>
        FCL | {prettifyString(bookingDetails.type)}
      </Row>
    );
  };

  const renderHaulageSelection = (label, initialValue) : React.ReactElement => {
    return (
      <div style={{ marginTop: 0 }} className={classes.flexRow}>
        <span className={classes.shipmentModalLabel} style={{ marginRight: '20px' }}>
          Haulage<Asterisk />
        </span>
        <Form.Item style={{ marginBottom: 0 }}>
          {getFieldDecorator(`${label.toLowerCase()}Haulage`, {
            initialValue,
          })(
            <Radio.Group
              className={classes.haulage}
              disabled={true}
            >
              {haulage?.map(item => (
                <Radio value={item.key}>{item.label}</Radio>
              ))}
            </Radio.Group>
          )}
        </Form.Item>
      </div>
    );
  };

  const renderPort = (label, initialValue, isDisabled, haulage = '') : React.ReactElement => {
    return (
      <Col span={12} style={{ display: 'flex' }}>
        <div className={classes.shipmentModalLabel} style={{ marginTop: '10px', width: '25%' }}>
          {label}<Asterisk />
        </div>
        <div style={{ width: '58%' }}>
          <Form.Item style={{ marginBottom: 0 }}>
            {getFieldDecorator(`${label.toLowerCase()}`, {
              rules: [{
                required: true, message: 'Required',
              }],
              initialValue,
            })(
              <Select
                disabled={isDisabled}
                onFocus={() => setState({ searchTerm: '' })}
                onSearch={(searchTerm) => setState({ searchTerm })}
                style={{ width: '100%' }}
                showSearch
                filterOption={(input, option) => true}
                labelInValue
                dropdownRender={menu => renderSelectDropDown(menu, isPortLoading)}
              >
                {ports?.map(port => (
                  <Select.Option key={JSON.stringify(port)}>{`${port.name} (${port.code})`}</Select.Option>
                ))}
              </Select>
            )}
          </Form.Item>
          {haulage && renderHaulageSelection(label, haulage)}
        </div>
      </Col>
    );
  };

  const renderPortDetails = () : React.ReactElement => {
    const portKey = isAmendOrNew ? bookingDetails : chargeDetails;
    return (
      <React.Fragment>
        <Row style={{ marginBottom: '12px' }}>
          {renderPort(
            'Origin',
            {
              key: bookingDetails?.originPortDetails?.code,
              label: `${bookingDetails?.originPortDetails?.name} (${bookingDetails?.originPortDetails?.code})`,
            },
            true,
            bookingDetails?.originPortDetails?.type === 'DRY' ?
              bookingDetails?.origin?.haulage : '',
          )}
          {renderPort(
            'Destination',
            {
              key: bookingDetails?.destinationPortDetails?.code,
              label: `${bookingDetails?.destinationPortDetails?.name} (${bookingDetails?.destinationPortDetails?.code})`,
            },
            true,
            bookingDetails?.destinationPortDetails?.type === 'DRY' ?
              bookingDetails?.destination?.haulage : '',
          )}
        </Row>
        <Row style={{ marginBottom: '12px' }}>
          {renderPort(
            'POL',
            {
              key: JSON.stringify({
                code: portKey?.portOfLoading?.code,
                name: portKey?.portOfLoading?.name,
              }),
              label: `${portKey?.portOfLoading?.name} (${portKey?.portOfLoading?.code})`,
            },
            !(bookingDetails?.originPortDetails?.type === 'DRY'),
          )}
          {renderPort(
            'POD',
            {
              key: JSON.stringify({
                code: portKey?.portOfDischarge?.code,
                name: portKey?.portOfDischarge?.name,
              }),
              label: `${portKey?.portOfDischarge?.name} (${portKey?.portOfDischarge?.code})`,
            },
            !(bookingDetails?.destinationPortDetails?.type === 'DRY'),
          )}
        </Row>
      </React.Fragment>
    );
  };

  const renderCargoProduct = () : React.ReactElement => {
    return (
      <Col span={12}>
        <Col span={6} className={classes.shipmentModalLabel} style={{ marginTop: '10px' }}>
          Cargo &amp; Product<Asterisk />
        </Col>
        <Col span={18} style={{ display: 'flex' }}>
          <Form.Item style={{ marginBottom: 0, width: '35%', marginRight: '2px' }}>
            {getFieldDecorator('cargoType', {
              initialValue: prettifyString(bookingDetails?.cargoType),
            })(
              <Select
                disabled={true}
                style={{ width: '100%' }}
              />
            )}
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, width: '42%' }}>
            {getFieldDecorator('productType', {
              initialValue: bookingDetails?.productType,
            })(
              <Select
                disabled={true}
                style={{ width: '100%' }}
              />
            )}
          </Form.Item>
        </Col>
      </Col>
    );
  };

  const renderProductDetails = () : React.ReactElement => {
    const productObj = bookingDetails?.productDetails || bookingDetails?.product;
    const hsCode = productObj?.hsCode;
    const productName = productObj?.name || productObj?.internalName;
    return (
      <Col span={12} style={{ display: 'flex' }}>
        <div className={classes.shipmentModalLabel} style={{ marginTop: '10px', width: '25%' }}>
          Product Details<Asterisk />
        </div>
        <Form.Item style={{ marginBottom: 0, width: '30%' }}>
          {getFieldDecorator('productDetails.hsCode', {
            rules: [{ required: true, message: 'Required' }],
            initialValue: hsCode,
          })(
            <Input
              placeholder="HS Code"
              style={{ width: '100%' }}
            />
          )}
        </Form.Item>
        <Form.Item style={{ marginBottom: 0, width: '44%', marginLeft: '6px' }}>
          {getFieldDecorator('productDetails.name', {
            rules: [{ required: true, message: 'Required' }],
            initialValue: productName,
          })(
            <Input
              placeholder="Product Name"
              style={{ width: '100%' }}
            />
          )}
        </Form.Item>
      </Col>
    );
  };

  const renderETD = () : React.ReactElement => {
    return (
      <Col span={12}>
        <Col span={6} className={classes.shipmentModalLabel} style={{ marginTop: '4px' }}>
          Estimated Time of Departure<Asterisk />
        </Col>
        <Col span={18}>
          <Form.Item style={{ marginBottom: 0 }}>
            {getFieldDecorator('preferredSailingStartdate', {
              rules: [{ required: true, message: 'Required' }],
              initialValue: getDateValue(
                bookingDetails.preferredSailingStartdate || bookingDetails.estimatedTimeOfDeparture
              ),
            })(
              <DatePicker
                format="DD MMM YYYY"
                allowClear={false}
                style={{ width: '77%' }}
              />,
            )}
          </Form.Item>
        </Col>
      </Col>
    );
  };

  const renderShippingLine = () : React.ReactElement => {
    return (
      <Row style={{ margin: '24px 0 12px 0' }}>
        <Col span={3} className={classes.shipmentModalLabel} style={{ marginTop: '10px' }}>
          Shipping Line<Asterisk />
        </Col>
        <Col span={21}>
          <Form.Item style={{ marginBottom: 0 }}>
            {getFieldDecorator('shippingLine', {
              initialValue: chargeDetails?.carrierDetails?.name,
            })(
              <Select
                disabled={true}
                style={{ width: '33%' }}
              />
            )}
          </Form.Item>
        </Col>
      </Row>
    );
  };

  const renderMovementSelection = (key, label, initialValue) : React.ReactElement => {
    return (
      <div style={{ marginTop: '-8px' }} className={classes.flexRow}>
        <Col span={4} className={classes.shipmentModalLabel} style={{ marginRight: '20px' }}>
          {key}<Asterisk />
        </Col>
        <Col span={20}>
          <Form.Item style={{ marginBottom: 0 }}>
            {getFieldDecorator(`${label.toLowerCase()}Haulage`, {
              initialValue,
            })(
              <Radio.Group
                className={classes.haulage}
                disabled={true}
              >
                {haulage?.map(item => (
                  <Radio value={item.key}>{item.label}</Radio>
                ))}
              </Radio.Group>
            )}
          </Form.Item>
        </Col>
      </div>
    );
  };

  const renderMovementType = (movementType) : React.ReactElement => {
    switch (movementType) {
      case 'Port to Door': return renderMovementSelection(
        'POD To Door Movement',
        'destinationMovement',
        bookingDetails.destination?.movementType,
      );
      case 'Door to Port': return renderMovementSelection(
        'Door To POL Movement',
        'originMovement',
        bookingDetails.origin?.movementType,
      );
      case 'Door to Door': return (
        <React.Fragment>
          {renderMovementSelection(
            'Door To POL Movement',
            'originMovement',
            bookingDetails.origin?.movementType,
          )}
          {renderMovementSelection(
            'POD To Door Movement',
            'destinationMovement',
            bookingDetails.destination?.movementType,
          )}
        </React.Fragment>
      )
      default: return <div></div>;
    };
  };

  const renderPickupAndDrop = () : React.ReactElement => {
    const movementTypeForCarrier = bookingDetails?.movementTypeForCarrier;
    return (
      <Row style={{ marginBottom: '8px' }}>
        <Col span={3} className={classes.shipmentModalLabel} style={{ marginTop: '10px' }}>
          Movement Type For Carrier<Asterisk />
        </Col>
        <Col span={21}>
          <Form.Item style={{ marginBottom: 0 }}>
            {getFieldDecorator('movementType', {
              initialValue: movementTypeForCarrier || 'Port to Port',
            })(
              <Select
                disabled={true}
                style={{ width: '33%' }}
              />,
            )}
          </Form.Item>
          {renderMovementType(movementTypeForCarrier)}
        </Col>
      </Row>
    );
  };

  const renderCountry = (label, initialValue) : React.ReactElement => {
    return (
      <Col span={12} style={{ display: 'flex' }}>
        <div className={classes.shipmentModalLabel} style={{ marginTop: '10px', width: '25%' }}>
          {label}<Asterisk />
        </div>
        <Form.Item style={{ marginBottom: 0, width: '58%' }}>
          {getFieldDecorator(`${label}`, {
            initialValue,
          })(
            <Select
              disabled={true}
              style={{ width: '100%' }}
            />
          )}
        </Form.Item>
      </Col>
    );
  };

  const renderCountryDetails = () : React.ReactElement => {
    const showOriginCountry = showMovementFields(['Door to Port', 'Door to Door']);
    const showDestinationCountry = showMovementFields(['Port to Door', 'Door to Door']);
    return (
      <Row style={{ margin: '16px 0' }}>
        {showOriginCountry && renderCountry(
          'Origin Country',
          bookingDetails?.originPortDetails?.countryName,
        )}
        {showDestinationCountry && renderCountry(
          'Destination Country',
          bookingDetails?.destinationPortDetails?.countryName,
        )}
      </Row>
    );
  };

  const renderContainerTypeSize = (item, ind, containerMaster) : React.ReactElement => {
    return (
      <React.Fragment>
        <div style={{ width: '6%', marginRight: '6px' }}>
        <div className={classes.formTitles}>
          Count
        </div>
        <Form.Item style={{ marginBottom: 0 }}>
          {getFieldDecorator(`containerDetails[${ind}].count`, {
            initialValue: item.count,
          })(
            <InputNumber
              style={{ width: '95%' }}
              disabled={true}
            />
          )}
        </Form.Item>
      </div>
      <div style={{ width: '12%' }}>
        <div className={classes.formTitles}>
          Type
        </div>
        <Form.Item style={{ marginBottom: 0 }}>
          {getFieldDecorator(`containerDetails[${ind}].type`, {
            initialValue: {
              key: containerMaster?.key?.type,
              label: containerMaster?.label?.type,
            }
          })(
            <Select
              style={{
                width: '95%',
              }}
              labelInValue
              disabled={true}
            />
          )}
        </Form.Item>
      </div>
      <div style={{ marginLeft: '-2px', width: '8%' }}>
        <div className={classes.formTitles}>
          Size
        </div>
        <Form.Item style={{ marginBottom: 0 }}>
          {getFieldDecorator(`containerDetails[${ind}].size`, {
            initialValue: {
              key: containerMaster?.key?.size,
              label: containerMaster?.label?.size,
            }
          })(
            <Select
              style={{ width: '95%' }}
              disabled={true}
              labelInValue
            />
          )}
        </Form.Item>
      </div>
      
      <span style={{
        margin: '34px 0px 0px 8px',
        borderLeft: '1px solid #666666',
        height: '32px',
      }}/>
      </React.Fragment>
    );
  };

  const renderWeight = (item, ind) : React.ReactElement => {
    return (
      <React.Fragment>
        <div style={{ width: '8%', marginLeft: '10px' }}>
          <div className={classes.formTitles}>
            Weight
          </div>
          <Form.Item style={{ marginBottom: 0 }}>
            {form.getFieldDecorator(`containerDetails[${ind}].weight.value`, {
              rules: [
                {
                  required: true, message: 'Required',
                },
              ],
              initialValue: item.weight?.value,
            })(
              <InputNumber
                type="number"
                placeholder="0.00"
                min={0}
                precision={2}
                style={{ width: '100%' }}
              />
            )}
          </Form.Item>
        </div>
        <div style={{ width: '9%', marginLeft: '6px' }}>
          <div  className={classes.formTitles}>
            Unit
          </div>
          <Form.Item style={{ width: '95%', marginBottom: '0px' }}>
            {getFieldDecorator(`containerDetails[${ind}].weight.unit`, {
              initialValue: item.weight?.unit || 'mt',
            })(
              <Select
                placeholder="Unit"
                style={{ width: '100%' }}
              >
                {weightsUnits?.map((unit) => (
                  <Select.Option key={unit}>{unit}</Select.Option>
                ))}
              </Select>
            )}
          </Form.Item>
        </div>
      </React.Fragment>
    );
  };

  const renderTemperature = (item, ind) : React.ReactElement => {
    const tempRange = containerTypeSizeTempRange?.[`${item.type} ${item.size}`];
    if (!tempRange) return <></>;
    return (
      <React.Fragment>
        <div style={{ width: '8%', marginLeft: '10px' }}>
          <div className={classes.formTitles}>
            Temperature
          </div>
          <Form.Item style={{ marginBottom: 0 }}>
            {form.getFieldDecorator(`containerDetails[${ind}].carriageTemperature`, {
              rules: [
                {
                  validator: (rule, value, cb) => {
                    if (!isValidNumber(value)) {
                      return cb('Please enter a valid number');
                    } else if (value > tempRange?.max) {
                      return cb(`Maximum ${tempRange?.max} allowed`);
                    } else if (value < tempRange?.min) {
                      return cb(`Minimum ${tempRange?.min} required`);
                    } else return cb();
                  },
                },
              ],
              initialValue: item.carriageTemperature,
            })(
              <InputNumber
                type="number"
                placeholder="0.00"
                precision={2}
                style={{ width: '100%' }}
              />
            )}
          </Form.Item>
        </div>
        <span style={{ margin: '4% 0 0 5px' }}>°C</span>
      </React.Fragment>
    );
  };

  const renderPackageDimension = (item, ind) : React.ReactElement => {
    return (
      <React.Fragment>
        {['Length', 'Breadth', 'Height'].map(key => (
          <div style={{ width: '9%', marginRight: '3px' }}>
            <div className={classes.formTitles}>
              {key}
            </div>
            <Form.Item style={{ marginBottom: 0 }}>
              {getFieldDecorator(`containerDetails[${ind}].packageDimension[${key.toLowerCase()}]`, {
                initialValue: item.packageDimension ? item.packageDimension[key.toLowerCase()] : undefined,
              })(
                <InputNumber
                  style={{ width: '100%' }}
                  disabled={true}
                />
              )}
            </Form.Item>
          </div>
        ))}
        <div style={{ width: '8%' }}>
          <div className={classes.formTitles}>
            Unit
          </div>
          <Form.Item style={{ marginBottom: 0 }}>
            {getFieldDecorator(`containerDetails[${ind}].packageDimension.unit`, {
              initialValue: item.packageDimension?.unit,
            })(
              <Select
                disabled={true}
                style={{ width: '100%' }}
              />
            )}
          </Form.Item>
        </div>
      </React.Fragment>
    );
  };

  const renderContainerDetails = () : React.ReactElement => {
    return (
      <Row style={{ marginBottom: '18px' }}>
        <Col span={3} className={classes.shipmentModalLabel} style={{ marginTop: '10px' }}>
          Container Details<Asterisk />
        </Col>
        <Col span={21} style={{ display: 'flex', flexDirection: 'column' }}>
          {bookingDetails.containerDetails?.map((item, ind) => {
            const containerMaster = containerList.find((container) => {
              return container.key.type === item.type &&
              container.key.size === item.size;
            }); 
            return (
              <div style={{ display: 'flex' }}>
                {renderContainerTypeSize(item, ind, containerMaster)}
                {renderWeight(item, ind)}
                {renderTemperature(item, ind)}
                {item.type === 'OPEN_TOP' && item?.packageDimension && renderPackageDimension(item, ind)}
              </div>
            )
          })}
        </Col>
      </Row>
    );
  };

  const renderAddress = (label, initialValue) : React.ReactElement => {
    return (
      <Col span={12} style={{ display: 'flex' }}>
        <Col span={6} className={classes.shipmentModalLabel} style={{ marginTop: '10px', width: '25%' }}>
          {label}<Asterisk />
        </Col>
        <Form.Item style={{ marginBottom: 0, width: '58%' }}>
          {getFieldDecorator(`${label}`, {
            initialValue,
          })(
            <TextArea
              disabled={true}
              rows={3}
              style={{ width: '100%' }}
            />
          )}
        </Form.Item>
      </Col>
    );
  };

  const showMovementFields = (arrayList) => {
    const movementTypeForCarrier = bookingDetails?.movementTypeForCarrier;
    return movementTypeForCarrier && arrayList.includes(movementTypeForCarrier);
  }

  const renderAddressDetails = () : React.ReactElement => {
    const showOriginAddress = showMovementFields(['Door to Port', 'Door to Door']);
    const showDestinationAddress = showMovementFields(['Port to Door', 'Door to Door']);
    return (
      <Row style={{ margin: '16px 0' }}>
        {showOriginAddress &&
          renderAddress(
            'Origin Address',
            bookingDetails?.origin?.address,
        )}
        {showDestinationAddress &&
          renderAddress(
            'Destination Address',
            bookingDetails?.destination?.address,
        )}
      </Row>
    );
  };

  const handleSave = () => {
    form.validateFields([
      'containerDetails',
      'productDetails',
      'preferredSailingStartdate',
      'pol',
      'pod',
    ], (err, values) => {
      if (err) return;
      onSave({
        preferredSailingStartdate: values.preferredSailingStartdate,
        containerDetails: values.containerDetails?.map((container, ind) => ({
          ...container,
          type: container.type?.key,
          size: container.size?.key,
          volume: bookingDetails.containerDetails[ind]?.volume,
        })),
        productDetails: values.productDetails,
        portOfLoading: JSON.parse(values.pol?.key || '{}'),
        portOfDischarge: JSON.parse(values.pod?.key || '{}'),
      });
      onCancel();
    });
  }

  const renderModalTitle = () : React.ReactElement => {
    return (
      <div className={classes.modalHeader}>
        <span className={classes.modalTitle}>Shipment Details</span>
        <div>
          <span
            className={classes.discard}
            onClick={onCancel}
          >
            Discard
          </span>
          <Button
            className={classes.saveButton}
            size="small"
            onClick={() => handleSave()}
          >
            Save
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Modal
      visible={isVisible}
      className={classes.shipmentModal}
      onCancel={onCancel}
      destroyOnClose
      closable={false}
      title={renderModalTitle()}
      footer={null}
    >
      {renderMode()}
      {renderShippingLine()}
      {renderPortDetails()}
      <Row style={{ marginBottom: '18px' }}>
        {renderCargoProduct()}
        {renderProductDetails()}
      </Row>
      {renderContainerDetails()}
      <Row style={{ marginBottom: '8px' }}>
        {renderETD()}
        
      </Row>
      {renderPickupAndDrop()}
      {renderCountryDetails()}
      {renderAddressDetails()}
    </Modal>
  );
};

const mapStateToProps = ({ masterData, inquiryReducer }, ownProps) => ({
  haulage: inquiryReducer.masterData.haulage,
  containerTypeSizeTempRange: inquiryReducer.masterData.containerTypeSizeTempRange,
  weightsUnits: inquiryReducer.masterData.weightsUnits,
  containerList: masterData.containerList,
});

export default connect(
  mapStateToProps,
  null,
)(React.memo(
  (withStyles(INTTRABookingConfirmationModalStyles)(Form.create()(EditINTTRAShipmentDetailsModal)))
));
